import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid2 } from '@mui/material';

import CustomTable from '@ui-components/Table/CustomTable';
import CustomTableRow from '@ui-components/Table/CustomTableRow';
import CustomTableHead from '@ui-components/Table/CustomTableHead';
import CustomTableCell from '@ui-components/Table/CustomTableCell';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomChip from '@ui-components/Chip/CustomChip';

import FilterIcon from '@assets/images/Filter.svg';
import SearchIcon from '@assets/images/search.svg';
import CustomButton from '@ui-components/Button/CustomButton';
import { rowsPerPage } from '../constants';

import { useLocation, useNavigate } from 'react-router-dom';
import CustomPagination from '@ui-components/Table/CustomPagination';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import useDebouncing from '../components/useDebouncing';
import { getEndItem, getSortIcon, getStartItem } from '../components/Sorting';
import { useGetFacilityUserList } from '../../API/hooks/FacilityUserHook';
import CustomTableBody from '@ui-components/Table/CustomTableBody';
import {
  FACILITY_NEW_USER_ROUTE_PATH,
  FACILITY_USER_ROUTE_PATH,
  FACILITY_USER_VIEW_ROUTE_PATH,
} from '../../routers/routePaths';
import EditOnlyIcon from '@assets/images/editOnly.svg';

import CustomDivider from '@qler-view/shared_lib/src/shared/lib/ui-components/Divider/CustomDivider';
import PartnerFacilitySelector from '../components/PartnerFacilitySelector';
import CustomPopover from '@qler-view/shared_lib/src/shared/lib/ui-components/Popover/CustomPopover';
import { useGetPartnerFacilityFilterData } from '../../API/hooks/PartnerFacilityFilterHook';
import type {
  PartnerFacilitiesFilter,
  SelectedState,
} from '../../API/services/PartnerFacilityFilterService';

import { RoleFilterSelector } from '../components/RoleFilterSelector';
import CustomLink from '@ui-components/Link/CustomLink';
import { hasRole } from '../login/storageUtility';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import { useFilters } from '../../routers/FilterContext';
import { CustomBadge } from '@qler-view/shared_lib/src';

const FacilityUserList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = localStorage.getItem('userId') ?? '0';
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortType, setSortType] = useState<'asc' | 'desc' | undefined>(
    undefined
  );
  const { filterContext, setFilterContext, getFilterCounts } = useFilters();
  const query = new URLSearchParams(location.search);
  const toast = query.get('toast');

  const [anchorFacilityUserEl, setAnchorFacilityUserEl] =
    useState<HTMLElement | null>(null);

  const [searchTermFacilityUser, setSearchTermFacilityUser] = useState<string>(
    filterContext.searchedValue
  );
  const debouncedSearchTerm = useDebouncing(searchTermFacilityUser, 500);

  const inputRef = useRef<HTMLInputElement>(null);
  const [partnerFacilityFilters, setPartnerFacilityFilters] = useState<
    PartnerFacilitiesFilter | undefined
  >();
  const { PartnerFacilityFilterData, refetch: refetchFilters } =
    useGetPartnerFacilityFilterData();

  useEffect(() => {
    if (PartnerFacilityFilterData) {
      setPartnerFacilityFilters(PartnerFacilityFilterData);
    }
  }, [PartnerFacilityFilterData]);

  const [selectedFacilities, setSelectedFacilities] = useState<SelectedState>(
    filterContext.facilities
  );

  const [selectedRoleIds, setSelectedRoleIds] = useState<number[] | undefined>(
    filterContext.roleIds
  );
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<
    number[] | undefined
  >([]);
  const [filterRoleIds, setFilterRoleIds] = useState<number[]>(
    filterContext.roleIds
  );
  const [facilityFilterSearch, setFacilityFilterSearch] = useState<string>('');

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, selectedFacilityIds, selectedRoleIds]);

  useEffect(() => {
    setSelectedFacilities(filterContext.facilities);
    const filterIds = Object.values(selectedFacilities).flat();
    // convert string → number

    setSelectedFacilityIds(filterIds);
    setFilterRoleIds(filterContext.roleIds);
    setSearchTermFacilityUser(filterContext.searchedValue);
  }, [filterContext]);

  const { FacilityUserListResponse, isLoading } = useGetFacilityUserList({
    userId: userId,
    page,
    size: rowsPerPage,
    sortField,
    sortType,
    searchTerm: debouncedSearchTerm,
    facilityId: selectedFacilityIds,
    roleId: selectedRoleIds,
  });

  const FacilityUserListCount = FacilityUserListResponse?.totalRecords ?? 0;

  const handleFacilityUserFilterClick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorFacilityUserEl(event.currentTarget);
    refetchFilters();
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus(); // Focus the input field
    }
  }, [searchTermFacilityUser]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage - 1);
  };

  const handleSort = (field: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortField === field && sortType === 'asc') {
      direction = 'desc';
    }
    setSortField(field);
    setSortType(direction);
  };

  const handleFacilityUserSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/^\s+/, '');
    value = value.replaceAll(/\s{2,}/g, ' ');
    setSearchTermFacilityUser(value);
    setFilterContext({
      ...filterContext,
      searchedValue: value,
    });
  };

  const handleFacilityFilterSearch = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value;
    value = value.replace(/^\s+/, '');
    value = value.replaceAll(/\s{2,}/g, ' ');

    const result = searchPartnerFacilities(PartnerFacilityFilterData, value);

    setPartnerFacilityFilters(result as PartnerFacilitiesFilter);
    setFacilityFilterSearch(value);
  };
  const handleViewFacilityUserInfo = (facilityUserId: number) => {
    navigate(FACILITY_USER_VIEW_ROUTE_PATH + `/${facilityUserId}`);
  };
  const handleEditFacilityUserInfo = (facilityUserId: number) => {
    navigate(FACILITY_NEW_USER_ROUTE_PATH + `/${facilityUserId}`, {
      replace: true,
      state: { isFacilityUserList: true },
    });
  };
  const handleApplyFilters = () => {
    setSelectedRoleIds(filterRoleIds);
    const filterIds = Object.values(selectedFacilities).flat();
    setSelectedFacilityIds(filterIds);
    setFilterContext({
      ...filterContext,
      facilities: selectedFacilities,
      roleIds: filterRoleIds,
    });
  };
  const searchPartnerFacilities = (
    data: PartnerFacilitiesFilter | undefined,
    searchText: string
  ) => {
    if (!searchText?.trim()) return data;

    const query = searchText.toLowerCase();

    const filteredPartnerFacilitiesMap = data?.partnerFacilitiesMap
      .map((partner) => {
        const partnerMatch = partner.partnerName.toLowerCase().includes(query);

        const matchedFacilities = partner.facilities.filter((facility) =>
          facility.name.toLowerCase().includes(query)
        );

        if (partnerMatch) {
          return { ...partner }; // keep all facilities
        }

        if (matchedFacilities.length > 0) {
          return {
            ...partner,
            facilities: matchedFacilities,
          };
        }

        return null;
      })
      .filter(Boolean);

    return {
      ...data,
      partnerFacilitiesMap: filteredPartnerFacilitiesMap,
    };
  };

  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (toast === 'success') {
      setMessage('Facility user saved successfully!');
      setOpenToast(true);
    } else if (toast === 'update') {
      setMessage('Facility user updated successfully!');
      setOpenToast(true);
    }
    navigate(FACILITY_USER_ROUTE_PATH, { replace: true });
  }, [toast, navigate]);

  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };

  const handleFacilityChange = (value: SelectedState) => {
    setSelectedFacilities(value);
  };

  const handleRoleChange = (value: number[]) => {
    setFilterRoleIds(value);
  };

  return (
    <Box>
      {openToast && (
        <CustomSnackbar
          id="facility-user-list-snackbar"
          successOrError={'success'}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={message}
        />
      )}
      <Box className="pt-20">
        <Grid2 container className="align-items-center">
          <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
            <CustomTypography
              id="page-title-facility-user"
              variant="h4"
              className="text-align-left"
            >
              Facility Users
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
            <Box className="flex float-right">
              <Box>
                <CustomTextField
                  id="txt-facility-user"
                  name="search-facility-user"
                  placeholder="Search Facility User"
                  size="small"
                  type="text"
                  value={searchTermFacilityUser}
                  onChange={handleFacilityUserSearch}
                  startAdornment={
                    <CustomIconButton
                      id="search-icon"
                      size="small"
                      icon={<SearchIcon />}
                    />
                  }
                />
              </Box>
              <Box className="ml-20">
                <CustomBadge
                  id={`notification`}
                  badgeContent={getFilterCounts(filterContext).totalCount}
                  color="error"
                  max={99}
                >
                  <CustomChip
                    aria-haspopup="true"
                    onClick={(e) => {
                      handleFacilityUserFilterClick(e);
                    }}
                    className="box-border2"
                    id="demo-chip"
                    label="Filters"
                    variant="outlined"
                    icon={<FilterIcon />}
                    iconPosition="left"
                  />
                </CustomBadge>
              </Box>

              {hasRole(['ops-admin']) && (
                <Box className="ml-20">
                  <CustomButton
                    variant="contained"
                    id="btn-add-facility-user"
                    color="primary"
                    size="large"
                    onClick={() => {
                      navigate(FACILITY_NEW_USER_ROUTE_PATH, { replace: true });
                    }}
                  >
                    Add Facility User
                  </CustomButton>
                </Box>
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="pt-20">
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CustomTypography
              id="loading-text"
              variant="h5"
              className="text-align-center pt-32"
              color="textDisabled"
            >
              Loading...
            </CustomTypography>
          </Box>
        ) : (
          <CustomTable id="table-facility-user">
            <CustomTableHead id="table-head-facility-user">
              <CustomTableRow id="facility-user-header">
                <CustomTableCell
                  id="table-cell-facility-user-id"
                  onClick={() => handleSort('facilityUserBusinessId')}
                >
                  USER ID&nbsp;
                  {sortField === 'facilityUserBusinessId' &&
                    getSortIcon(sortType)}
                </CustomTableCell>

                <CustomTableCell
                  id="table-cell-user-first-name"
                  onClick={() => handleSort('firstName')}
                >
                  FIRST NAME&nbsp;
                  {sortField === 'firstName' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-user-last-name"
                  onClick={() => handleSort('lastName')}
                >
                  LAST NAME&nbsp;
                  {sortField === 'lastName' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-primary-email"
                  onClick={() => handleSort('primaryEmail')}
                >
                  PRIMARY EMAIL &nbsp;
                  {sortField === 'primaryEmail' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-associated-facility-name"
                  onClick={() => handleSort('facilityName')}
                >
                  FACILITY NAME &nbsp;
                  {sortField === 'facilityName' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-role"
                  onClick={() => handleSort('role')}
                >
                  ROLE &nbsp;
                  {sortField === 'role' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-user-status"
                  onClick={() => handleSort('status')}
                >
                  STATUS &nbsp;
                  {sortField === 'status' && getSortIcon(sortType)}
                </CustomTableCell>
                {hasRole(['ops-admin']) && (
                  <CustomTableCell id="table-cell-facility-action">
                    ACTIONS &nbsp;
                  </CustomTableCell>
                )}
              </CustomTableRow>
            </CustomTableHead>
            {FacilityUserListResponse &&
            FacilityUserListResponse?.facilityUsersList.length > 0 ? (
              <CustomTableBody id="table-body-facility-user">
                {FacilityUserListResponse?.facilityUsersList.map(
                  (facilityusers) => (
                    <CustomTableRow
                      key={facilityusers.id}
                      id={`table-row-facility-user-${facilityusers.id}`}
                      className={facilityusers.isLocked ? 'locked-row' : ''}
                    >
                      <CustomTableCell
                        id={`table-cell-facility-user-id-${facilityusers.id}`}
                      >
                        <CustomLink
                          id={`link-facility-user-id-${facilityusers.id}`}
                          color="primary"
                          onClick={() =>
                            handleViewFacilityUserInfo(facilityusers.id)
                          }
                        >
                          {facilityusers.facilityUserBusinessId}
                        </CustomLink>
                      </CustomTableCell>

                      <CustomTableCell
                        id={`table-cell-facilityuser-first-name-${facilityusers.id}`}
                      >
                        {facilityusers.firstName || 'N/A'}
                      </CustomTableCell>
                      <CustomTableCell
                        id={`table-cell-facilityuser-last-name-${facilityusers.id}`}
                      >
                        {facilityusers.lastName || 'N/A'}
                      </CustomTableCell>

                      <CustomTableCell
                        id={`table-cell-facility-user-email-${facilityusers.id}`}
                      >
                        {facilityusers.primaryEmail || 'N/A'}
                      </CustomTableCell>
                      <CustomTableCell
                        id={`table-cell-user-facility-name-${facilityusers.id}`}
                      >
                        {facilityusers.facilityName || 'N/A'}
                      </CustomTableCell>
                      <CustomTableCell
                        id={`table-cell-user-role-${facilityusers.id}`}
                      >
                        {facilityusers.role || 'N/A'}
                      </CustomTableCell>
                      <CustomTableCell
                        id={`table-cell-facility-user-status-${facilityusers.id}`}
                      >
                        {facilityusers.status}
                      </CustomTableCell>
                      {hasRole(['ops-admin']) && (
                        <CustomTableCell
                          id={`table-cell-partner-action-${facilityusers.id}`}
                        >
                          <Box className="flex align-items-center">
                            <Box className="ml-8">
                              <CustomIconButton
                                id="edit-icon"
                                size="small"
                                icon={<EditOnlyIcon />}
                                onClick={() => {
                                  handleEditFacilityUserInfo(facilityusers.id);
                                }}
                              />
                            </Box>
                          </Box>
                        </CustomTableCell>
                      )}
                    </CustomTableRow>
                  )
                )}
              </CustomTableBody>
            ) : (
              <CustomTableBody id="table-body-facility-user">
                <CustomTableRow id="table-row-facility-user">
                  <CustomTableCell id="table-cell-facility-user-id" colspan={9}>
                    <Box className="flex flex-justify-center align-items-center">
                      <CustomTypography
                        id="no-facility-users-found"
                        variant="body2"
                        color="textDisabled"
                      >
                        No Facility Users Found
                      </CustomTypography>
                    </Box>
                  </CustomTableCell>
                </CustomTableRow>
              </CustomTableBody>
            )}
          </CustomTable>
        )}
        {FacilityUserListResponse && FacilityUserListCount > 0 && (
          <Box className="mt-32">
            <Grid2 container className="align-items-center">
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <Box className="flex justify-content-start">
                  <CustomTypography
                    id="showing-row-count-text"
                    variant="body2"
                    color="textDisabled"
                  >
                    Showing {getStartItem(page, rowsPerPage)} -{' '}
                    {getEndItem(page, rowsPerPage, FacilityUserListCount)} of{' '}
                    {FacilityUserListCount} Facility Users
                  </CustomTypography>
                </Box>
              </Grid2>
              {FacilityUserListCount > rowsPerPage && (
                <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                  <Box display="flex" justifyContent="end">
                    <CustomPagination
                      id="facility-user-list-pagination"
                      count={Math.ceil(FacilityUserListCount / rowsPerPage)}
                      page={page + 1}
                      onPageChange={handlePageChange}
                      color="standard"
                    />
                  </Box>
                </Grid2>
              )}
            </Grid2>
          </Box>
        )}
      </Box>
      <Box>
        <CustomPopover
          open={Boolean(anchorFacilityUserEl)}
          anchorEl={anchorFacilityUserEl}
          onClose={() => setAnchorFacilityUserEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          className="mt-4"
        >
          <Box className="p-24 width-500px">
            <Box className="text-align-left">
              <CustomTypography
                id="intake-form-filter-title"
                variant="h4"
                color="textPrimary"
              >
                Filters
              </CustomTypography>
              <Box className="pt-8">
                <CustomTypography
                  id="intake-form-filter-subtitle"
                  variant="body2"
                  color="textDisabled"
                >
                  Filter records by facility and role
                </CustomTypography>
              </Box>
            </Box>
            <Box className="pt-16">
              <CustomDivider />
            </Box>
            <Box className="flex flex-column">
              <Grid2
                container
                justifyContent="center"
                spacing={2}
                className="width-100"
              >
                <Grid2 size={{ xs: 6 }}>
                  <Box className="flex-between-center pt-24">
                    <CustomTypography
                      id="intake-form-facility-mapping-title"
                      variant="body2"
                      color="textDisabled"
                    >
                      FACILITIES
                    </CustomTypography>
                  </Box>

                  <Box className="pt-20 pb-20">
                    <CustomTextField
                      id="intake-form-facility-search"
                      variant="outlined"
                      size="small"
                      value={facilityFilterSearch}
                      placeholder="Search facilities..."
                      startAdornment={
                        <CustomIconButton
                          id="search-icon"
                          size="small"
                          icon={<SearchIcon />}
                        />
                      }
                      onChange={handleFacilityFilterSearch}
                    />
                  </Box>

                  <Box>
                    <PartnerFacilitySelector
                      data={partnerFacilityFilters}
                      value={selectedFacilities}
                      onChange={handleFacilityChange}
                      showPartnerCheckbox={true}
                      singleFacilitySelection={false}
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 6 }}>
                  <Box>
                    <Box className="flex-between-center pt-24">
                      <Box>
                        <CustomTypography
                          id="intake-form-role-mapping-title"
                          variant="body2"
                          color="textDisabled"
                        >
                          ROLES
                        </CustomTypography>
                      </Box>
                    </Box>
                    <Box className="pt-8">
                      <RoleFilterSelector
                        data={PartnerFacilityFilterData}
                        value={filterRoleIds}
                        onChange={handleRoleChange}
                        userType="facilityuser"
                      />
                    </Box>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
            <Box>
              <Grid2 container>
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <Box className="display-flex justify-end">
                    <Box className="pr-12">
                      <CustomButton
                        id="cancel-facility-user-button"
                        onClick={() => {
                          setAnchorFacilityUserEl(null);
                          setFacilityFilterSearch('');
                          setSelectedFacilities({});
                          setFilterRoleIds([]);
                          setSelectedRoleIds([]);
                          setSelectedFacilityIds([]);
                          setPartnerFacilityFilters(PartnerFacilityFilterData);
                          setFilterContext({
                            ...filterContext,
                            facilities: {},
                            roleIds: [],
                          });
                        }}
                        variant="outlined"
                      >
                        Clear
                      </CustomButton>
                    </Box>
                    <Box>
                      <CustomButton
                        id="apply-facility-user-filter-button"
                        onClick={() => {
                          setAnchorFacilityUserEl(null);
                          handleApplyFilters();
                        }}
                        variant="contained"
                      >
                        Apply
                      </CustomButton>
                    </Box>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
          </Box>
        </CustomPopover>
      </Box>
    </Box>
  );
};

export default FacilityUserList;
